import { html } from '../html.js';
import { DEFAULT_PRINT_OPTIONS } from '../print-options.js';
import { t } from '../strings.js';
import { docDate, field, fields, footer, header, ltr, money } from './parts.js';
import { notesBlock, party, qtyCell } from './record-parts.js';
const STAGE_KEYS = {
    draft: 'stageDraft',
    confirmed: 'stageConfirmed',
    shipped: 'stageShipped',
    in_transit: 'stageInTransit',
    customs_clearance: 'stageCustomsClearance',
    received: 'stageReceived',
    closed: 'stageClosed',
    cancelled: 'stageCancelled',
};
const METHOD_KEYS = {
    sea: 'methodSea',
    air: 'methodAir',
    land: 'methodLand',
};
const ALLOC_KEYS = {
    by_value: 'allocByValue',
    by_quantity: 'allocByQuantity',
    by_weight: 'allocByWeight',
    by_volume: 'allocByVolume',
};
const CHARGE_KEYS = {
    freight: 'chargeFreight',
    duty: 'chargeDuty',
    insurance: 'chargeInsurance',
    handling: 'chargeHandling',
    other: 'chargeOther',
};
/**
 * PUR-06's import order as a printed document — wave 5E.
 *
 * The paper answers the screen's one question, "what did each item really
 * cost?": the stage rail with the shipment's place on it, the lines at their
 * LANDED unit cost beside the supplier's price (the gap between them IS the
 * information), each charge in the currency it was billed in and in the
 * shipment's, and the three-row summary. Base figures carry the base code so
 * no two currencies are ever silently combined. A DRAFT or CANCELLED shipment
 * says so in the title block, as every record document does.
 */
export function importOrderHtml(data, locale, company, generatedAt, generatedBy, print = DEFAULT_PRINT_OPTIONS) {
    const stamp = data.stage === 'draft' || data.stage === 'cancelled'
        ? html `<span class="doc-badge">${t(STAGE_KEYS[data.stage], locale)}</span>`
        : null;
    const c = data.currency;
    return html `
    ${header(company, t('importOrderTitle', locale), stamp)}
    ${fields([
        field(t('importRef', locale), ltr(data.code)),
        field(t('orderDate', locale), docDate(data.orderDate)),
        field(t('status', locale), t(STAGE_KEYS[data.stage], locale)),
        field(t('supplierCode', locale), ltr(data.partnerCode)),
        ...(data.purchaseOrderCode === null ? [] : [field(t('purchaseOrderRef', locale), ltr(data.purchaseOrderCode))]),
        ...(data.manualRef === null ? [] : [field(t('manualRef', locale), ltr(data.manualRef))]),
        field(t('shipDate', locale), docDate(data.shipDate)),
        field(t('arriveDate', locale), docDate(data.arriveDate)),
        field(t('incoterm', locale), ltr(data.incoterm)),
        field(t('shipMethod', locale), t(METHOD_KEYS[data.shipMethod], locale)),
        ...(data.carrier === null || data.carrier === '' ? [] : [field(t('carrier', locale), data.carrier)]),
        ...(data.trackingNo === null || data.trackingNo === '' ? [] : [field(t('trackingNo', locale), ltr(data.trackingNo))]),
        field(t('warehouse', locale), data.warehouseName),
        ...(data.currency === data.baseCurrency
            ? []
            : [field(t('exchangeRate', locale), ltr(`1 ${data.baseCurrency} = ${data.rate} ${data.currency}`))]),
        field(t('allocMethod', locale), t(ALLOC_KEYS[data.allocMethod], locale)),
    ])}
    ${party(t('supplier', locale), data.partnerName)}

    <section class="doc-notes">
      <strong>${t('stages', locale)}</strong><br />
      ${data.stages.map((stage, index) => html `${index === 0 ? null : ' › '}${stage === data.stage
        ? html `<strong>${t(STAGE_KEYS[stage], locale)}</strong>`
        : t(STAGE_KEYS[stage], locale)}`)}
    </section>

    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('lineNo', locale)}</th>
          ${print.showItemCode ? html `<th>${t('itemCode', locale)}</th>` : null}
          <th>${t('description', locale)}</th>
          <th>${t('hsCode', locale)}</th>
          <th>${t('origin', locale)}</th>
          <th class="amount">${t('qty', locale)}</th>
          <th class="amount">${t('unitPrice', locale)}</th>
          <th class="amount">${t('lineCost', locale)}</th>
          <th class="amount">${t('chargeShare', locale)}</th>
          <th class="amount">${t('landedUnitCost', locale)}</th>
          <th class="amount">${t('inBase', locale)}</th>
        </tr>
      </thead>
      <tbody>
        ${data.lines.map((line) => html `
            <tr>
              <td>${ltr(String(line.lineNo))}</td>
              ${print.showItemCode ? html `<td>${ltr(line.itemCode)}</td>` : null}
              <td>${line.description}</td>
              <td>${line.hsCode === null ? null : ltr(line.hsCode)}</td>
              <td>${line.originCountry}</td>
              <td class="amount">${qtyCell(line.qty, line.unit)}</td>
              <td class="amount">${money(line.unitPrice, c)}</td>
              <td class="amount">${money(line.lineCost, c)}</td>
              <td class="amount">${money(line.chargeShare, c)}</td>
              <td class="amount">${money(line.landedUnitCost, c)}</td>
              <td class="amount">${money(line.landedUnitCostBase, data.baseCurrency)}</td>
            </tr>
          `)}
      </tbody>
    </table>

    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('charges', locale)}</th>
          <th class="amount">${t('amount', locale)}</th>
          <th class="amount">${t('inOrderCurrency', locale)}</th>
          <th>${t('billRef', locale)}</th>
        </tr>
      </thead>
      <tbody>
        ${data.charges.map((charge) => html `
            <tr>
              <td>${charge.category === 'other' && charge.label ? charge.label : t(CHARGE_KEYS[charge.category], locale)}</td>
              <td class="amount">${money(charge.amount, charge.currency)}</td>
              <td class="amount">${money(charge.amountOrder, c)}</td>
              <td>${charge.billCode === null ? null : ltr(charge.billCode)}</td>
            </tr>
          `)}
      </tbody>
    </table>

    <section class="doc-totals">
      <div class="row"><span>${t('itemsCost', locale)}</span>${money(data.itemsCost, c)}</div>
      <div class="row"><span>${t('chargesTotal', locale)}</span>${money(data.chargesTotal, c)}</div>
      <div class="row grand"><span>${t('totalLanded', locale)}</span>${money(data.totalLanded, c)}</div>
      <div class="row"><span>${t('inBase', locale)}</span>${money(data.totalLandedBase, data.baseCurrency)}</div>
    </section>

    ${notesBlock(t('cancelReason', locale), data.cancelReason)}
    ${notesBlock(t('notes', locale), data.notes)}
    ${footer(locale, generatedAt, company.name, generatedBy)}
  `;
}
//# sourceMappingURL=import-order.js.map